import React, { useState, useEffect } from 'react';
import { Activity, Zap, RefreshCw, CheckCircle, AlertCircle, XCircle, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import API_URL from '../config/API_URL';

const StatusPage = () => {
  const [serverStatus, setServerStatus] = useState({
    status: 'checking',
    lastChecked: null,
    responseTime: null
  });
  
  const [aiStatus, setAiStatus] = useState({
    status: 'checking',
    lastChecked: null,
    description: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    
    checkAllStatus();
    
    const interval = setInterval(() => {
      checkAllStatus();
    }, 24 * 60 * 60 * 1000);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      clearInterval(interval);
    };
  }, []);

  function capitalizeFirstLetter(string) {
    if (!string) return ""; // Handle empty or null strings
    return string.charAt(0).toUpperCase() + string.slice(1);
    }

  const checkServerStatus = async () => {
    const startTime = Date.now();
    
    try {
      const response = await fetch(`${API_URL}/api/health`, {
        method: 'GET',
        credentials: 'include'
      });
      
      const responseTime = Date.now() - startTime;
      
      if (response.ok) {
        setServerStatus({
          status: 'operational',
          lastChecked: new Date().toISOString(),
          responseTime: responseTime
        });
      } else {
        setServerStatus({
          status: 'degraded',
          lastChecked: new Date().toISOString(),
          responseTime: responseTime
        });
      }
    } catch (error) {
      setServerStatus({
        status: 'down',
        lastChecked: new Date().toISOString(),
        responseTime: null
      });
    }
  };

  const checkAIStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/api-status`, {
        method: 'GET',
        credentials: 'include'
      });
      
      const data = await response.json();
      
      let status = 'operational';
      if (data.status === 'minor' || data.status === 'major') {
        status = 'degraded';
      } else if (data.status === 'critical') {
        status = 'down';
      }
      
      setAiStatus({
        status: status,
        lastChecked: new Date().toISOString(),
        description: data.description || 'All systems operational'
      });
    } catch (error) {
      setAiStatus({
        status: 'unknown',
        lastChecked: new Date().toISOString(),
        description: 'Unable to check AI status'
      });
    }
  };

  const checkAllStatus = async () => {
    setLoading(true);
    await Promise.all([
      checkServerStatus(),
      checkAIStatus()
    ]);
    setLoading(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational':
        return {
          bg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          border: '#10b981',
          text: '#10b981',
          glow: 'rgba(16, 185, 129, 0.3)'
        };
      case 'degraded':
        return {
          bg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          border: '#f59e0b',
          text: '#f59e0b',
          glow: 'rgba(245, 158, 11, 0.3)'
        };
      case 'down':
        return {
          bg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          border: '#ef4444',
          text: '#ef4444',
          glow: 'rgba(239, 68, 68, 0.3)'
        };
      default:
        return {
          bg: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
          border: '#64748b',
          text: '#64748b',
          glow: 'rgba(100, 116, 139, 0.3)'
        };
    }
  };

  const getStatusIcon = (status, size = 24) => {
    switch (status) {
      case 'operational':
        return <CheckCircle size={size} />;
      case 'degraded':
        return <AlertCircle size={size} />;
      case 'down':
        return <XCircle size={size} />;
      default:
        return <Activity size={size} />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'operational':
        return 'Operational';
      case 'degraded':
        return 'Degraded';
      case 'down':
        return 'Down';
      case 'checking':
        return 'Checking...';
      default:
        return 'Unknown';
    }
  };

  const formatLastChecked = (timestamp) => {
    if (!timestamp) return 'Never';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  const overallStatus = 
    serverStatus.status === 'down' || aiStatus.status === 'down' ? 'down' :
    serverStatus.status === 'degraded' || aiStatus.status === 'degraded' ? 'degraded' :
    serverStatus.status === 'operational' && aiStatus.status === 'operational' ? 'operational' :
    'checking';

  const styles = {
    page: {
      minHeight: '100vh',
      background: '#0a0a0a',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
      padding: isMobile ? '2rem 1rem' : '3rem 2rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    container: {
      maxWidth: '800px',
      width: '100%',
    },
    header: {
      textAlign: 'center',
      marginBottom: isMobile ? '2rem' : '3rem',
    },
    logo: {
      width: isMobile ? '100px' : '120px',
      height: isMobile ? '80px' : '100px',
      objectFit: 'contain',
      filter: 'brightness(0) invert(1) drop-shadow(0 10px 30px rgba(0,0,0,0.3))',
      marginBottom: '0.5rem',
    },
    title: {
      fontSize: isMobile ? '1.5rem' : '2rem',
      fontWeight: '700',
      marginBottom: '0.5rem',
      color: '#e2e8f0',
    },
    subtitle: {
      fontSize: isMobile ? '0.9rem' : '1rem',
      color: '#64748b',
    },
    // MAIN HERO STATUS
    heroStatus: {
      background: 'rgba(255, 255, 255, 0.03)',
      border: '2px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '24px',
      padding: isMobile ? '3rem 2rem' : '4rem 3rem',
      marginBottom: '2rem',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
    },
    heroStatusGlow: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '300px',
      height: '300px',
      borderRadius: '50%',
      filter: 'blur(60px)',
      opacity: 0.3,
      pointerEvents: 'none',
    },
    heroIcon: {
      marginBottom: '1.5rem',
      position: 'relative',
      zIndex: 1,
    },
    heroTitle: {
      fontSize: isMobile ? '2.5rem' : '3.5rem',
      fontWeight: '800',
      marginBottom: '0.5rem',
      letterSpacing: '-0.02em',
      position: 'relative',
      zIndex: 1,
    },
    heroSubtitle: {
      fontSize: isMobile ? '1rem' : '1.25rem',
      color: '#94a3b8',
      marginBottom: '2rem',
      position: 'relative',
      zIndex: 1,
    },
    // COMPACT SERVICES GRID
    servicesGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
      gap: '1rem',
      marginBottom: '1.5rem',
    },
    serviceCard: {
      background: 'rgba(255, 255, 255, 0.03)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      borderRadius: '16px',
      padding: isMobile ? '1.25rem' : '1.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      transition: 'all 0.2s ease',
    },
    serviceIcon: {
      width: '40px',
      height: '40px',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    serviceContent: {
      flex: 1,
      minWidth: 0,
    },
    serviceName: {
      fontSize: '0.95rem',
      fontWeight: '600',
      color: '#e2e8f0',
      marginBottom: '0.25rem',
    },
    serviceStatus: {
      fontSize: '0.85rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontWeight: '600',
    },
    serviceMeta: {
      fontSize: '0.75rem',
      color: '#64748b',
      marginTop: '0.25rem',
    },
    // COMPACT FOOTER INFO
    footerInfo: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem',
      padding: isMobile ? '1.25rem' : '1.5rem',
      background: 'rgba(255, 255, 255, 0.02)',
      border: '1px solid rgba(255, 255, 255, 0.05)',
      borderRadius: '16px',
      fontSize: '0.85rem',
      color: '#94a3b8',
    },
    updateButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.75rem 1.5rem',
      background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
      border: 'none',
      borderRadius: '12px',
      fontSize: '0.9rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      color: 'white',
      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
      whiteSpace: 'nowrap',
    },
    lastUpdate: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Compact Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={styles.header}
        >
          <img 
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="Loop Logo" 
            style={styles.logo}
          />
          <h1 style={styles.title}>System Status</h1>
          <p style={styles.subtitle}>Real-time monitoring</p>
        </motion.div>

        {/* HERO STATUS - The Star of the Show */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          style={styles.heroStatus}
        >
          {/* Animated glow effect */}
          <div 
            style={{
              ...styles.heroStatusGlow,
              background: getStatusColor(overallStatus).glow,
            }}
          />
          
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
            style={styles.heroIcon}
          >
            <div style={{ color: getStatusColor(overallStatus).text }}>
              {getStatusIcon(overallStatus, isMobile ? 64 : 80)}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h2 style={{
              ...styles.heroTitle,
              color: getStatusColor(overallStatus).text
            }}>
              {overallStatus === 'operational' && 'All Systems Operational'}
              {overallStatus === 'degraded' && 'Degraded Performance'}
              {overallStatus === 'down' && 'System Issues Detected'}
              {overallStatus === 'checking' && 'Checking Status...'}
            </h2>
            <p style={styles.heroSubtitle}>
              {overallStatus === 'operational' && 'Everything is running smoothly'}
              {overallStatus === 'degraded' && 'Some services experiencing issues'}
              {overallStatus === 'down' && 'We\'re working to resolve this'}
              {overallStatus === 'checking' && 'Please wait...'}
            </p>
          </motion.div>

          {/* Update Button in Hero */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            onClick={checkAllStatus}
            disabled={loading}
            style={{
                ...styles.updateButton,
                opacity: loading ? 0.7 : 1,
                cursor: loading ? 'not-allowed' : 'pointer',
                position: 'relative',
                zIndex: 1,
                margin: '0 auto',  // Add this line
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 20px rgba(59, 130, 246, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
              }
            }}
          >
            <motion.div
              animate={loading ? { rotate: 360 } : { rotate: 0 }}
              transition={loading ? { duration: 1, repeat: Infinity, ease: 'linear' } : {}}
            >
              <RefreshCw size={16} />
            </motion.div>
            {loading ? 'Checking...' : 'Update Now'}
          </motion.button>
        </motion.div>

        {/* Compact Services Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={styles.servicesGrid}
        >
          {/* Server Service */}
          <div
            style={{
              ...styles.serviceCard,
              borderLeft: `3px solid ${getStatusColor(serverStatus.status).border}`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              e.currentTarget.style.transform = 'translateX(4px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
              e.currentTarget.style.transform = 'translateX(0)';
            }}
          >
            <div
              style={{
                ...styles.serviceIcon,
                background: 'rgba(59, 130, 246, 0.1)',
              }}
            >
              <Activity size={20} color="#3b82f6" />
            </div>
            <div style={styles.serviceContent}>
              <div style={styles.serviceName}>Server</div>
              <div style={{
                ...styles.serviceStatus,
                color: getStatusColor(serverStatus.status).text
              }}>
                {getStatusIcon(serverStatus.status, 14)}
                {getStatusText(serverStatus.status)}
              </div>
              {serverStatus.responseTime && (
                <div style={styles.serviceMeta}>
                  {serverStatus.responseTime}ms response time
                </div>
              )}
            </div>
          </div>

          {/* AI Service */}
          <div
            style={{
              ...styles.serviceCard,
              borderLeft: `3px solid ${getStatusColor(aiStatus.status).border}`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              e.currentTarget.style.transform = 'translateX(4px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
              e.currentTarget.style.transform = 'translateX(0)';
            }}
          >
            <div
              style={{
                ...styles.serviceIcon,
                background: 'rgba(139, 92, 246, 0.1)',
              }}
            >
              <Zap size={20} color="#8b5cf6" />
            </div>
            <div style={styles.serviceContent}>
              <div style={styles.serviceName}>AI Services</div>
              <div style={{
                ...styles.serviceStatus,
                color: getStatusColor(aiStatus.status).text
              }}>
                {getStatusIcon(aiStatus.status, 14)}
                {getStatusText(aiStatus.status)}
              </div>
              <div style={styles.serviceMeta}>
                API {capitalizeFirstLetter(aiStatus.status)}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Warning Message if Degraded/Down */}
        {aiStatus.description && aiStatus.status !== 'operational' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              padding: '1rem 1.25rem',
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '12px',
              fontSize: '0.9rem',
              color: '#fbbf24',
              lineHeight: 1.5,
              marginBottom: '1.5rem',
            }}
          >
            <strong>Notice:</strong> {aiStatus.description}
          </motion.div>
        )}

        {/* Compact Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          style={styles.footerInfo}
        >
          <div style={styles.lastUpdate}>
            <Clock size={14} />
            <span>
              Updated {formatLastChecked(serverStatus.lastChecked || aiStatus.lastChecked)}
            </span>
          </div>
          <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
            Auto-refresh: Every 24 hours
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default StatusPage;
